import React, { ReactNode, useContext } from "react";
import styled, { css } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { LoaderCircle, Plus, SlidersHorizontal, X } from "lucide-react";
import Button from "common/Buttons/Button";
import BulkActions from "common/Table/BulkActions";
import Drawer from "common/Drawers/Drawer";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import { PageWrapperFiltersContext } from "common/Home/PageWrapper/PageWrapperFilters.context.ts";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_SECTION_GAP,
  usePageIntro,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook";
import { hexToRGB } from "@shared/utils/misc";
import { NEW_LOOK_RADIUS, getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  closedTradesFilters,
  openTradesFilters,
} from "@shared/slices/filters.slice";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import { useTradesHeader } from "features/trades/common/TradesHeader";
import NewLookTradesTabs from "features/trades/NewLook/NewLookTradesTabs.tsx";
import NewLookTradesOverview from "features/trades/NewLook/NewLookTradesOverview.tsx";

interface NewLookTradesDesktopProps {
  isOpenTrades: boolean;
  mainContent: ReactNode[];
  header: ReturnType<typeof useTradesHeader>;
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
}

/**
 * New-look desktop layout of the trades page: the Open/Closed tabs and the
 * page actions share one toolbar row, a compact overview row sits above the
 * trades list, and filters open in a side sheet so the list keeps its width.
 */
const NewLookTradesDesktop = ({
  isOpenTrades,
  mainContent,
  header,
  filtersOpen,
  setFiltersOpen,
}: NewLookTradesDesktopProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const dispatch = useDispatch();
  const {
    showExportButton,
    exportButton,
    onCreateClick,
    noExchangeConnectedModal,
  } = header;
  const {
    selectedTradesIDs,
    setSelectedTradesIDs,
    actionButtonsData,
    isBulkMode,
    exitBulkMode,
    selectAll,
    isAllRowsSelected,
  } = useContext(TradeActionsContext)!;
  const { getTradesQuery } = useContext(TradesPageContext)!;
  const { filters: pageFilters } = useContext(PageWrapperFiltersContext);
  const isSubUser = useIsSubUser();
  const user = useSelectUser();
  const filters = isOpenTrades ? openTradesFilters : closedTradesFilters;
  const numActiveFilters = useSelector(
    filters.selectors.selectNumActiveFilters
  );
  const isFetching = !!getTradesQuery?.isFetching;
  // The toolbar wraps on narrow screens, so the button is always shown.
  const showFiltersButton = !!pageFilters;
  const showCreateButton = !isSubUser && !user?.isWhopExperience();

  usePageIntro(
    {
      actions: (
        <Toolbar>
          {showFiltersButton && (
            <FiltersButton
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              $active={filtersOpen || numActiveFilters > 0}
              aria-expanded={filtersOpen}
            >
              {isFetching ? (
                <Spinner size={15} strokeWidth={NEW_LOOK_ICON_STROKE} />
              ) : (
                <SlidersHorizontal
                  size={15}
                  strokeWidth={NEW_LOOK_ICON_STROKE}
                />
              )}
              {t("filters.open_filters")}
              {numActiveFilters > 0 && <Count>{numActiveFilters}</Count>}
            </FiltersButton>
          )}
          {showFiltersButton && numActiveFilters > 0 && (
            <ResetButton
              type="button"
              onClick={() => dispatch(filters.actions.selectAll(undefined))}
            >
              <X size={14} strokeWidth={2} />
              {tCommon("filters.common.reset")}
            </ResetButton>
          )}
          {showExportButton && <QuietButton>{exportButton}</QuietButton>}
          {showCreateButton && (
            <Button type="primary" onClick={onCreateClick}>
              <Plus size={16} strokeWidth={2} />
              {t("actions.create_trade")}
            </Button>
          )}
        </Toolbar>
      ),
      footer: <NewLookTradesTabs />,
    },
    [
      showFiltersButton,
      filtersOpen,
      isFetching,
      numActiveFilters,
      showExportButton,
      exportButton,
      showCreateButton,
      onCreateClick,
      filters,
      t,
      tCommon,
    ]
  );

  return (
    <Page>
      {isBulkMode && (
        <BulkBar>
          <BulkActions
            actionButtonsData={actionButtonsData}
            unselectAll={() => setSelectedTradesIDs([])}
            selectAll={selectAll}
            numSelected={selectedTradesIDs.length}
            onCancel={exitBulkMode}
            isAllSelected={isAllRowsSelected}
          />
        </BulkBar>
      )}
      <Main>
        {!isBulkMode && <NewLookTradesOverview isOpenTrades={isOpenTrades} />}
        {mainContent}
      </Main>
      {pageFilters && (
        <Drawer
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          size={FILTERS_WIDTH}
          closable={false}
          destroyOnHidden={false}
          // The filters column loads the filter values the list depends on.
          forceRender={true}
          styles={DRAWER_STYLES}
        >
          <FiltersColumn
            filtersSelectors={pageFilters.selectors}
            filtersActions={pageFilters.actions}
            selectLoadingStates={pageFilters.loadingStatesSelector}
            hideFilterTypes={pageFilters.hideFilterTypes}
            filterTypeToTitle={pageFilters.filterTypeToTitle}
            isLoading={pageFilters.loading}
            closeMobileFilters={() => setFiltersOpen(false)}
          />
        </Drawer>
      )}
      {noExchangeConnectedModal}
    </Page>
  );
};

export default NewLookTradesDesktop;

const FILTERS_WIDTH = 360;
const DRAWER_STYLES = { body: { padding: "18px 24px 32px" } };

const Page = styled.div`
  padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_PAGE_GUTTER_VAR};
`;
const BulkBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  margin: -20px calc(-1 * ${NEW_LOOK_PAGE_GUTTER_VAR}) 16px;
  padding: 12px ${NEW_LOOK_PAGE_GUTTER_VAR};
  background: ${(p) => getNewLookTokens(p.theme).canvas};
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
  min-width: 0;
`;
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  .ant-btn {
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    gap: 6px;
  }
`;
/** A quiet 32px pill, the same family as the sort control. */
const pill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: ${(p) => p.theme.borders.card};
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  color: ${(p) => p.theme.colors.titleColor};
  font: inherit;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  cursor: pointer;
  transition: color 150ms, background-color 150ms, border-color 150ms;
  svg {
    color: ${(p) => p.theme.colors.secondaryText};
    flex: none;
  }
  &:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primaryBlue};
    outline-offset: 1px;
  }
`;
const FiltersButton = styled.button<{ $active?: boolean }>`
  ${pill};
  ${(p) =>
    p.$active &&
    css`
      border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.45)};
      background: ${p.theme.colors.lightBlue};
      color: ${p.theme.colors.primaryBlue};
      svg {
        color: ${p.theme.colors.primaryBlue};
      }
      &:hover {
        background: ${p.theme.colors.lightBlue};
      }
    `}
`;
const ResetButton = styled.button`
  ${pill};
  border-color: transparent;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  padding: 0 10px;
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
  }
`;
/** Tones the export button down to the quiet pill style. */
const QuietButton = styled.span`
  display: inline-flex;
  && .ant-btn {
    border: ${(p) => p.theme.borders.card};
    background: ${(p) => p.theme.colors.cardBgColor};
    color: ${(p) => p.theme.colors.titleColor};
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
    box-shadow: none;
    svg {
      color: ${(p) => p.theme.colors.secondaryText};
    }
    &:hover {
      background: ${(p) => p.theme.colors.cardLightBgColor};
      color: ${(p) => p.theme.colors.titleColor};
    }
  }
`;
const Spinner = styled(LoaderCircle)`
  animation: newLookSpin 0.9s linear infinite;
  @keyframes newLookSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const Count = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.primaryBlue};
  color: ${(p) => p.theme.colors.buttonText};
  font-size: 11px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  text-align: center;
  font-variant-numeric: tabular-nums;
`;
